import { ENV } from "@/constants/env";
import instance, { ApiResponse } from "./instance";

// Dashboard 타입 정의 - 백엔드 응답 구조에 맞게 수정
interface DashboardStats {
  comId: number;
  comNm: string;
  // FAQ는 최상위에 있음
  faqStats: {
    totalCount: number;
    activeCount: number;
    categoryDistribution: Record<string, number>;
    completenessRate: number;
  };
  // 기간별 통계는 periodStats 안에 있음
  periodStats: {
    trialStats: {
      totalCount: number;
      thisMonthCount: number;
      pendingCount: number;
      approvedCount: number;
      completedCount: number;
      conversionRate: number;
      growthRate: number;
    };
    qnaStats: {
      totalCount: number;
      thisMonthCount: number;
      todayCount: number;
      answeredCount: number;
      pendingCount: number;
      responseRate: number;
      averageResponseTime: number;
      growthRate: number;
    };
    ga4Traffic: {
      totalSessions: number;
      totalUsers: number;
      newUsers: number;
      returningUsers: number;
      avgBounceRate: number;
      avgSessionDuration: number;
      thisMonthSessions: number | string;
      lastMonthSessions: number | string;
      growthRate: number;
      topTrafficSources: Array<{
        source: string;
        medium: string;
        sessions: number;
        users: number;
      }>;
      dailyStats: Array<{
        date: string;
        sessions: number;
        users: number;
        bounceRate: number;
      }>;
      hasConnection: boolean;
    };
    period: string;
    startDate: string;
    endDate: string;
    dailyStats: Array<{
      date: string;
      trialCount: number;
      qnaCount: number;
      sessionCount: number;
      faqViewCount: number;
    }>;
    overallScore: number;
    overallGrowthRate: number;
  };
  lastUpdated: string;
}

interface DashboardStatsQueryDto {
  period: string;
  startDate?: string;
  endDate?: string;
}

interface CompaniesComparisonStatsDto {
  comId: number;
  comNm: string;
  stats: DashboardStats;
}

interface CompaniesComparisonStatsQueryDto {
  startDate?: string;
  endDate?: string;
  comIds?: number[];
}

// ✅ PREFIX_PATH 사용 필수 (하드코딩 금지)
const PREFIX_PATH = `${ENV.PRE_FIX}/${ENV.ADMIN_PATH}`;

export interface DashboardApiResponse {
  success: boolean;
  message: string;
  data: DashboardStats;
  error: string | null;
}

export interface CompareStatsApiResponse {
  success: boolean;
  message: string;
  data: DashboardStats[];
  error: string | null;
}

/**
 * 대시보드 API 클라이언트
 * @description 슈퍼 관리자용 대시보드 통계 API를 호출합니다.
 */
export const dashboardApi = {
  /**
   * 도장별 종합 통계 조회
   * @param comId 도장 ID
   * @param period 조회 기간 (7days, 30days, 90days)
   * @param startDate 시작 날짜 (선택사항)
   * @param endDate 종료 날짜 (선택사항)
   * @returns 종합 대시보드 통계
   */
  async getCompanyStats(
    comId: number,
    period: "7days" | "30days" | "90days" = "30days",
    startDate?: string,
    endDate?: string
  ): Promise<DashboardStats> {
    // ✅ PREFIX_PATH 사용 필수 (하드코딩 금지)
    const apiUrl = `${PREFIX_PATH}/dashboard/stats/${comId}`;

    console.log("[API_CALL]", {
      url: apiUrl,
      method: "GET",
      params: { period, startDate, endDate },
      PREFIX_PATH,
      baseURL: ENV.API_URL,
    });

    const params = new URLSearchParams();
    params.append("period", period);
    if (startDate) params.append("startDate", startDate);
    if (endDate) params.append("endDate", endDate);

    try {
      const response: any = await instance.get(
        `${apiUrl}?${params.toString()}`
      );

      console.log("[API_RESPONSE]", {
        url: apiUrl,
        success: response.success,
        dataCount: response.data ? 1 : 0,
      });

      if (!response.success) {
        console.error("[API_ERROR]", {
          success: response.success,
          message: response.message,
          error: response.error,
        });

        throw new Error(
          response.message || "Failed to fetch dashboard statistics"
        );
      }

      return response.data;
    } catch (error: any) {
      console.error("[ERROR]", { api: "getDashboardStats", error });
      const errorMessage =
        error instanceof Error ? error.message : "Unknown error";
      throw new Error(
        error?.response?.data?.message ||
          errorMessage ||
          "Failed to fetch dashboard statistics"
      );
    }
  },

  /**
   * 다중 도장 통계 비교 조회
   * @param comIds 도장 ID 목록
   * @param period 조회 기간
   * @returns 도장별 통계 목록
   */
  async compareCompaniesStats(
    comIds: number[],
    period: "7days" | "30days" | "90days" = "30days"
  ): Promise<DashboardStats[]> {
    try {
      // ✅ PREFIX_PATH 사용 필수 (하드코딩 금지)
      const apiUrl = `${PREFIX_PATH}/dashboard/compare`;

      console.log("[API_CALL]", {
        url: apiUrl,
        method: "GET",
        params: { comIds: comIds.join(","), period },
        PREFIX_PATH,
        baseURL: ENV.API_URL,
      });

      const params = new URLSearchParams();
      params.append("comIds", comIds.join(","));
      params.append("period", period);

      const response: any = await instance.get(
        `${apiUrl}?${params.toString()}`
      );

      console.log("[API_RESPONSE]", {
        url: apiUrl,
        success: response.success,
        companiesCount: response.data?.length,
      });

      if (!response.success) {
        throw new Error(
          response.message || "Failed to fetch companies comparison statistics"
        );
      }

      return response.data;
    } catch (error: unknown) {
      console.error("[ERROR]", { api: "compareCompaniesStats", error });
      const errorMessage =
        error instanceof Error ? error.message : "Unknown error";
      throw new Error(
        (error as any)?.response?.data?.message ||
          errorMessage ||
          "Failed to fetch companies comparison statistics"
      );
    }
  },

  /**
   * 통계 카드 데이터 생성
   * @description 기존 useDashboard 훅과 호환되는 형태로 데이터를 변환합니다.
   * @param stats 대시보드 통계 데이터
   * @returns 통계 카드 배열
   */
  generateStatisticsCards(stats: DashboardStats) {
    if (!stats || !stats.periodStats) return [];

    return [
      {
        title: "트라이얼 접수",
        value: stats.periodStats.trialStats.totalCount,
        description: "이번 달 트라이얼 신청 건수",
        trend: `${
          stats.periodStats.trialStats.growthRate >= 0 ? "+" : ""
        }${stats.periodStats.trialStats.growthRate.toFixed(1)}%`,
        type: "trial" as const,
      },
      {
        title: "FAQ 등록",
        value: stats.faqStats.totalCount,
        description: "등록된 FAQ 총 건수",
        trend: "0.0%", // FAQ는 성장률이 없으므로 기본값
        type: "faq" as const,
      },
      {
        title: "QNA 접수",
        value: stats.periodStats.qnaStats.totalCount,
        description: "이번 달 문의 접수 건수",
        trend: `${
          stats.periodStats.qnaStats.growthRate >= 0 ? "+" : ""
        }${stats.periodStats.qnaStats.growthRate.toFixed(1)}%`,
        type: "qna" as const,
      },
      {
        title: "홈페이지 트래픽",
        value: stats.periodStats.ga4Traffic.totalSessions,
        description: "이번 달 세션 수",
        trend: `${
          stats.periodStats.ga4Traffic.growthRate >= 0 ? "+" : ""
        }${stats.periodStats.ga4Traffic.growthRate.toFixed(1)}%`,
        type: "traffic" as const,
      },
    ];
  },
};
